import Error404 from 'pages/Error/Error404';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchCampaignApplications } from 'slices/campaigns/applications/thunk';
import { defaultApplicationFilters, defaultShortlistedFilters } from 'slices/campaigns/applications/constants';
import { defaultCollaborationFilters } from 'slices/campaigns/collaborations/reducer';
import { defaultInvitesFilters } from 'slices/campaigns/invites/reducer';
import { fetchCampaign } from 'slices/campaigns/thunk';
import { AppDispatch, RootState } from 'store';
import { Nav, NavLink, NavItem } from 'reactstrap';
import InfluencerPostModal from 'Components/influencer/InfluencerPostModal';
import ApplicationView from 'Components/campaigns/applications/ApplicationView';
import SocialPostModal from 'Components/social-account/SocialPostModal';
import {
    closeInfluencerPostModal,
    closeSocialPostModal,
    setHeaderCampaignData,
    setLastActiveCampaignTab,
    toggleApplicationView,
} from 'slices/ui/reducer';
import AccessControl from 'wrappers/AccessControl';
import { PRIVILEGE } from 'constants/privilege';
import { encodeBase64, generateUniqueKey } from 'utils/helpers';
import Loader from 'Components/Common/Loader';
import 'pages/Campaign/campaign.scss';
import './style.scss';

interface CampaignLayoutProps {
    children: React.ReactNode;
    filters?: React.ReactNode;
    filtersToolbar?: React.ReactNode;
}

const CampaignLayout: React.FC<CampaignLayoutProps> = ({ filters, filtersToolbar, children }) => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { campaignId } = useParams();

    const loading = useSelector((state: RootState) => state.campaigns.loading);

    const campaignDetail = useSelector((state: RootState) => state.campaigns.items[campaignId!]);
    const campaignData = campaignDetail?.data ?? null;

    const creatorRecommendationAllowed = campaignData?.campaign?.creator_recommendation_allowed ?? false;

    const campaignApplicationsState = useSelector(
        (state: RootState) => state.campaignApplications.campaigns[campaignId!]
    );
    const applicationFilters = useSelector((state: RootState) => state.campaignApplications.filtersApplications);
    const shortlistedFilters = useSelector((state: RootState) => state.campaignApplications.filtersShortlisted);

    const applicationsFilterId = generateUniqueKey(
        { ...(applicationFilters ?? defaultApplicationFilters), bookmarked: 'n' },
        ['page']
    );
    const defaultShortlistedFilterId = useMemo(
        () => generateUniqueKey({ ...(shortlistedFilters ?? defaultShortlistedFilters), bookmarked: 'y' }, ['page']),
        [shortlistedFilters]
    );

    const filteredApplicationsCount =
        campaignApplicationsState?.filtered_lists?.[applicationsFilterId]?.count ?? 0;
    const shortlistedTabCount =
        campaignApplicationsState?.filtered_lists?.[defaultShortlistedFilterId]?.count ?? 0;
    const totalApplicationsCount = campaignApplicationsState?.total ?? 0;

    const shortlistTabPulse = useSelector((state: RootState) => state.ui.shortlistTabPulse);
    const [shortlistBadgePulse, setShortlistBadgePulse] = useState(false);

    useEffect(() => {
        if (shortlistTabPulse > 0) {
            setShortlistBadgePulse(true);
            const timer = setTimeout(() => setShortlistBadgePulse(false), 650);
            return () => clearTimeout(timer);
        }
    }, [shortlistTabPulse]);

    const influencerPostModalVisibile = useSelector((state: RootState) => state.ui.influencerPostModal);
    const applicationView = useSelector((state: RootState) => state.ui.applicationView);
    const socialPostModal = useSelector((state: RootState) => state.ui.socialPostModal);

    const collaborationFilters = useSelector((state: RootState) => state.campaignCollaborations.filters);
    const invitesFilters = useSelector((state: RootState) => state.campaignInvites.filters);

    const totalCollaborationsCount =
        useSelector(
            (state: RootState) =>
                state.campaignCollaborations.campaigns[campaignId!]?.filtered_lists?.[
                    state.campaignCollaborations.applied_filter_id!
                ]?.count
        ) ?? 0;

    const collaborationsTabCount =
        totalCollaborationsCount > 0 ? totalCollaborationsCount : campaignData?.campaign?.collaborations_count ?? 0;

    useEffect(() => {
        if (campaignId && !campaignData) {
            dispatch(fetchCampaign({ campaignId }));
        }
    }, [campaignId, dispatch, campaignData]);

    useEffect(() => {
        if (!campaignId || campaignData?.campaign?.mode === 'offline') return;

        dispatch(
            fetchCampaignApplications({
                campaignId,
                filters: { ...defaultShortlistedFilters, page: 1 },
                silent: true,
            })
        );
    }, [campaignId, campaignData?.campaign?.mode, dispatch]);

    useEffect(() => {
        if (campaignData) {
            dispatch(setHeaderCampaignData({ campaign: campaignData }));
        }

        return () => {
            if (campaignData) {
                dispatch(setHeaderCampaignData({ campaign: null, detailPage: false }));
            }
        };
    }, [campaignData, dispatch]);

    const getTabId = useMemo(() => {
        const path = location.pathname;
        if (path.includes('/shortlisted')) return 'shortlisted';
        if (path.includes('/applications')) return 'applications';
        if (path.includes('/collaborations')) return 'collaborations';
        if (path.includes('/insights')) return 'insights';
        if (path.includes('/recommended-creators')) return 'recommended-creators';
        if (path.includes('/invites')) return 'invites';
        return '';
    }, [location.pathname]);

    const getPageTitle = useMemo(() => {
        if (!campaignData) return '';

        switch (getTabId) {
            case 'applications':
                return 'Applications Received';
            case 'shortlisted':
                return 'Shortlisted';
            case 'collaborations':
                return 'Collaborations';
            case 'insights':
                return 'Campaign Insights';
            case 'recommended-creators':
                return 'Recommended Creators';
            case 'invites':
                return 'Invites';
            default:
                return 'Campaign';
        }
    }, [campaignData, getTabId]);

    useEffect(() => {
        if (['applications', 'shortlisted', 'collaborations', 'insights', 'recommended-creators', 'invites'].includes(getTabId)) {
            dispatch(
                setLastActiveCampaignTab(
                    getTabId as 'applications' | 'shortlisted' | 'collaborations' | 'insights' | 'recommended-creators' | 'invites'
                )
            );
        }
    }, [getTabId, dispatch]);

    useEffect(() => {
        if (campaignData?.campaign?.mode === 'offline' && getTabId === 'applications') {
            navigate(`/campaign/${campaignId}`);
        }
    }, [campaignData, getTabId, campaignId, navigate]);

    const getRecentFiltersQuery = (dataSet: 'applications' | 'collaborations' | 'shortlisted' | 'invites') => {
        let filterPayload: Record<string, unknown> | null = null;

        switch (dataSet) {
            case 'applications':
                filterPayload = {
                    ...(applicationFilters ?? defaultApplicationFilters),
                    bookmarked: 'n',
                    page: 1,
                };
                break;
            case 'shortlisted':
                filterPayload = {
                    ...(shortlistedFilters ?? defaultShortlistedFilters),
                    bookmarked: 'y',
                    page: 1,
                };
                break;
            case 'invites':
                filterPayload = {
                    ...(invitesFilters ?? defaultInvitesFilters),
                    page: 1,
                };
                break;
            case 'collaborations':
                filterPayload = collaborationFilters ?? defaultCollaborationFilters;
                break;
            default:
                filterPayload = null;
        }

        if (!filterPayload) return '';
        return `?filters=${encodeBase64(filterPayload)}`;
    };

    if (loading && !campaignData) return <Loader />;

    if ((!loading && !campaignData) || !campaignId) return <Error404 />;

    return (
        <div>
            <div className="campaign-page">
                <h1 className="campaign-page-title">{getPageTitle}</h1>
                <div className="d-flex justify-content-between align-items-center campaign-page-info flex-column flex-lg-row mt-4 gap-2 gap-lg-0">
                    <Nav tabs className="nav nav-tabs nav-tabs-custom nav-primary nav-justified campaign-nav w-100 w-lg-50">
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={`campaign-nav-link d-flex align-items-center ${getTabId === 'applications' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/applications${getRecentFiltersQuery('applications')}`)}
                                >
                                    Applications
                                    {totalApplicationsCount ? (
                                        <span className="campaign-nav-tab-badge text-white d-flex align-items-center gap-1" style={{ borderRadius: '4px' }}>
                                            {filteredApplicationsCount} <span>of</span> {totalApplicationsCount}
                                        </span>
                                    ) : (
                                        <span className="campaign-nav-tab-badge text-white d-flex align-items-center gap-1">
                                            {campaignData?.campaign?.applications_count ?? 0}
                                        </span>
                                    )}
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={`campaign-nav-link ${getTabId === 'shortlisted' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/shortlisted${getRecentFiltersQuery('shortlisted')}`)}
                                >
                                    <span className="d-inline-flex align-items-center">
                                        Shortlisted
                                        {shortlistedTabCount > 0 && (
                                            <span
                                                className={`campaign-nav-tab-badge ${shortlistBadgePulse ? 'campaign-nav-tab-badge--pulse' : ''
                                                    }`}
                                            >
                                                {shortlistedTabCount}
                                            </span>
                                        )}
                                    </span>
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.COLLABORATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={`campaign-nav-link ${getTabId === 'collaborations' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/collaborations${getRecentFiltersQuery('collaborations')}`)}
                                >
                                    <span className="d-inline-flex align-items-center">
                                        Collaborations
                                        {collaborationsTabCount > 0 && (
                                            <span className="campaign-nav-tab-badge">{collaborationsTabCount}</span>
                                        )}
                                    </span>
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.PERFORMANCE.INDEX}>
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={`campaign-nav-link ${getTabId === 'insights' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/insights`)}
                                >
                                    Insights
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.PERFORMANCE.INDEX} ifCondition={creatorRecommendationAllowed}>
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={`campaign-nav-link ${getTabId === 'recommended-creators' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/recommended-creators`)}
                                >
                                    Recommended
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.INVITE.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className="campaign-nav-item">
                                <NavLink
                                    style={{ cursor: 'pointer' }}
                                    className={`campaign-nav-link ${getTabId === 'invites' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/invites${getRecentFiltersQuery('invites')}`)}
                                >
                                    Invites
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                    </Nav>
                    {filters}
                </div>
                {Boolean(filtersToolbar) && (
                    <div className="campaign-page-filters">{filtersToolbar}</div>
                )}
                <div className="campaign-page-content my-3">{children}</div>
            </div>
            <InfluencerPostModal
                isOpen={!!influencerPostModalVisibile}
                onClose={() => dispatch(closeInfluencerPostModal())}
                postId={influencerPostModalVisibile?.postId ?? null}
                collaborationId={influencerPostModalVisibile?.collaborationId}
                influencer={influencerPostModalVisibile?.influencer}
                socialAccount={influencerPostModalVisibile?.socialAccount}
                status={influencerPostModalVisibile?.status}
                preventAction={!!influencerPostModalVisibile?.preventAction}
            />
            <ApplicationView
                isOpen={!!applicationView}
                applicationId={applicationView?.applicationId!}
                campaignId={applicationView?.campaignId!}
                socialAccountId={applicationView?.socialAccountId!}
                toggleOffcanvas={() => dispatch(toggleApplicationView(null))}
            />
            <SocialPostModal
                isOpen={!!socialPostModal}
                onClose={() => dispatch(closeSocialPostModal())}
                postId={socialPostModal?.postId}
                influencer={socialPostModal?.influencer}
                socialAccount={socialPostModal?.socialAccount}
                disableComments
            />
        </div>
    );
};

export default CampaignLayout;
